<template>
  <v-app>
    <v-app-bar app color="#b71c1c" dark dense>
      <v-btn icon @click="$router.push('/')" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-avatar size="32" class="mr-2">
        <img src="/img/icons/android-chrome-512x512.png" alt="Logo" />
      </v-avatar>
      <responsive-toolbar-title class="white--text">
        Noormarkun VPK
      </responsive-toolbar-title>
    </v-app-bar>

    <v-main>
      <v-container
        class="fill-height d-flex justify-center align-center"
        style="max-width: 900px"
      >
        <v-card v-if="loading" loading class="pa-5">
          <v-card-text>Ladataan lomaketta...</v-card-text>
        </v-card>

        <v-card v-else-if="error" class="pa-5">
          <v-alert type="error">{{ error }}</v-alert>
        </v-card>

        <v-card v-else-if="formData" class="pa-0 pa-sm-4" style="width: 100%">
          <v-card-title class="headline text-center d-block word-break">
            NOORMARKUN VPK RY – JÄSENTIETOLOMAKE
          </v-card-title>
          <v-card-text>
            <v-alert type="info" text class="mb-4">
              Tämä lomake on tarkoitettu Noormarkun VPK ry:n jäsenille.
              Alaikäisen kohdalla huoltajan tiedot ja suostumus ovat pakollisia.
            </v-alert>

            <v-form ref="form" v-model="valid">
              <!-- 1. Jäsentiedot -->
              <h3 class="title mb-3">1. Jäsentiedot</h3>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Etunimi"
                    v-model="formData.firstName"
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Sukunimi"
                    v-model="formData.lastName"
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dobFormatted"
                        label="Syntymäaika"
                        placeholder="pp.kk.vvvv"
                        prepend-icon="mdi-calendar"
                        v-bind="attrs"
                        v-on="on"
                        :rules="requiredRules"
                        @blur="updateDob"
                        @change="updateDob"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.dob"
                      ref="picker"
                      :first-day-of-week="1"
                      :max="new Date().toISOString().substr(0, 10)"
                      min="1900-01-01"
                      @change="
                        $refs.menu.save(form.dob);
                        checkAge();
                      "
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Osoite"
                    v-model="form.address"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Puhelinnumero"
                    v-model="form.phone"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Sähköposti"
                    v-model="form.email"
                    :rules="emailRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" v-if="showIban">
                  <v-text-field
                    label="Tilinumero (IBAN)"
                    v-model="form.iban"
                    :rules="showIban ? ibanRules : []"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Ruoka-aineallergiat"
                    v-model="form.allergies"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Ajokorttiluokka"
                    v-model="form.drivingLicense"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Siviiliammatti"
                    v-model="form.civilOccupation"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-checkbox
                    v-model="form.professionalDriverQualification"
                    label="Kuljettajan ammattipätevyys"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    label="Osasto (Jäsenasioidenhoitaja täyttää)"
                    v-model="departmentsStr"
                    readonly
                    filled
                    rows="1"
                    auto-grow
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-divider class="my-6"></v-divider>

              <!-- 2. Huoltaja -->
              <h3 class="title mb-3">
                {{
                  isMinor
                    ? "2. Alaikäisen huoltajan / huoltajien tiedot"
                    : "2. Lähiomaisen tiedot"
                }}
              </h3>
              <div v-if="isMinor" class="mb-2 red--text font-weight-bold">
                Pakollinen, koska jäsen on alle 18 v
              </div>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    :label="isMinor ? 'Huoltajan nimi' : 'Lähiomaisen nimi'"
                    v-model="form.guardianName"
                    :rules="isMinor ? requiredRules : []"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="
                      isMinor
                        ? 'Huoltajan puhelinnumero'
                        : 'Lähiomaisen puhelinnumero'
                    "
                    v-model="form.guardianPhone"
                    :rules="isMinor ? requiredRules : []"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="
                      isMinor
                        ? 'Huoltajan sähköposti'
                        : 'Lähiomaisen sähköposti'
                    "
                    v-model="form.guardianEmail"
                    :rules="isMinor ? emailRules : []"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-6"></v-divider>

              <!-- 3. Suostumukset -->
              <h3 class="title mb-3">3. Suostumukset ja luvat</h3>
              <v-checkbox
                v-model="form.consentDataProcessing"
                :rules="[(v) => !!v || 'Suostumus vaaditaan']"
                label="Annan suostumukseni henkilötietojeni käsittelyyn jäsenrekisterissä ja viestintäkanavissa WhatsApp, VPK App, Secapp, Procountor sekä tietojen tallentamiseen Haka-järjestelmään."
              ></v-checkbox>
              <v-checkbox
                v-model="form.consentImages"
                label="Annan suostumukseni kuvien julkaisuun (verkkosivut, Facebook, lehdet tms.)"
              ></v-checkbox>
              <v-checkbox
                v-model="form.consentNamePublish"
                label="Annan suostumukseni nimitietojen julkaisuun palokunnan ilmoitustaululla."
              ></v-checkbox>
              <v-checkbox
                v-if="isMinor"
                v-model="form.consentGuardian"
                :rules="
                  isMinor ? [(v) => !!v || 'Huoltajan suostumus vaaditaan'] : []
                "
                label="(Alaikäisen kohdalla) Huoltaja antaa suostumuksen yllä oleviin kohtiin."
              ></v-checkbox>

              <v-divider class="my-6"></v-divider>

              <!-- 4. Lisätiedot -->
              <h3 class="title mb-3">4. Lisätiedot (täytetään tarvittaessa)</h3>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    label="Koulutukset/kelpoisuudet"
                    hint="Esim. Pelastustoiminnan peruskurssi, Ensiapukurssi, Tieliikennepelastamisen kurssi jne."
                    v-model="form.qualifications"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    label="Varusteiden kokotiedot"
                    hint="Esim. koot S, M, L, XL jne."
                  ></v-text-field>
                </v-col>
                <!--<v-col cols="12">
                  <v-text-field
                    label="Hälytysryhmät ja profiilit"
                    v-model="form.alarmGroups"
                  ></v-text-field>
                </v-col>-->
              </v-row>

              <v-divider class="my-6"></v-divider>

              <!-- 5. Allekirjoitus -->
              <h3 class="title mb-3">5. Allekirjoitus</h3>

              <v-alert type="info" outlined class="mb-4">
                <div class="text-subtitle-2 mb-2 font-weight-bold">
                  Huomio: Muuttuneista tiedoista ilmoittaminen
                </div>
                <p class="mb-2">
                  Jäsen on velvollinen ilmoittamaan muuttuneista tiedoista
                  viipymättä oman osaston osastonjohtajalle.
                </p>
                <ul class="ml-4">
                  <li>
                    Hälytysosasto:
                    <a href="mailto:halytysosasto@noormarkunvpk.fi"
                      >halytysosasto@noormarkunvpk.fi</a
                    >
                  </li>
                  <li>
                    Nuoriso-osasto:
                    <a href="mailto:nuoriso-osasto@noormarkunvpk.fi"
                      >nuoriso-osasto@noormarkunvpk.fi</a
                    >
                  </li>
                  <li>
                    Varhaisnuoriso-osasto:
                    <a href="mailto:varhaisnuoriso-osasto@noormarkunvpk.fi"
                      >varhaisnuoriso-osasto@noormarkunvpk.fi</a
                    >
                  </li>
                  <li>
                    Tukiosasto:
                    <a href="mailto:tukiosasto@noormarkunvpk.fi"
                      >tukiosasto@noormarkunvpk.fi</a
                    >
                  </li>
                  <li>
                    Veteraaniosasto:
                    <a href="mailto:veteraaniosasto@noormarkunvpk.fi"
                      >veteraaniosasto@noormarkunvpk.fi</a
                    >
                  </li>
                </ul>
              </v-alert>

              <p class="caption">Päiväys: {{ currentDate }}</p>
              <div class="mb-4">
                <p>
                  Jäsenen allekirjoitus
                  {{ isMinor ? "(tai Huoltajan allekirjoitus)" : "" }}
                </p>

                <div v-if="signatureUrl" class="mb-2 text-center">
                  <img
                    :src="signatureUrl"
                    style="max-height: 150px; border: 1px solid #ccc"
                  />
                  <div class="mt-2">
                    <v-btn small text @click="clearSignature">Muokkaa</v-btn>
                  </div>
                </div>

                <div
                  v-show="!signatureUrl"
                  style="
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    background: #fff;
                  "
                >
                  <VueSignaturePad
                    width="100%"
                    height="200px"
                    ref="signaturePad"
                    :options="{ penColor: 'black' }"
                  />
                </div>
                <div v-if="!signatureUrl" class="d-flex mt-2">
                  <v-btn small text @click="clearSignaturePad">Tyhjennä</v-btn>
                  <v-btn small text @click="undoSignaturePad">Kumoa</v-btn>
                  <v-spacer></v-spacer>
                  <v-btn small color="primary" @click="saveSignaturePad"
                    >OK - Tallenna allekirjoitus</v-btn
                  >
                </div>
                <div v-if="!signatureUrl" class="caption red--text mt-1">
                  Muista painaa OK tallentaaksesi allekirjoituksen ennen
                  lähettämistä.
                </div>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 6. Tietosuojalauseke -->
              <h3 class="title mb-3">TIETOSUOJALAUSEKE</h3>
              <div class="text-body-2 mb-4">
                <p class="mb-2">
                  Noormarkun VPK ry sitoutuu EU:n tietosuoja-asetuksen (GDPR)
                  noudattamiseen. Henkilötietoja käsitellään vain jäsenyyden ja
                  toiminnan edellyttämissä rajoissa. Tietoja säilytetään
                  jäsenyyden ajan ja poistetaan, kun jäsenyys päättyy tai
                  suostumus perutaan.
                </p>
                <p class="mb-2">
                  Rekisteröidyllä on oikeus tarkastaa, oikaista ja poistaa
                  tietonsa sekä perua suostumuksensa milloin tahansa ottamalla
                  yhteyttä rekisterinpitäjään. Rekisteröidyllä on myös oikeus
                  tehdä valitus valvontaviranomaiselle.
                </p>
                <p class="mb-2">
                  Tietoja ei luovuteta kolmansille osapuolille ilman
                  lainmukaista perustetta. Tietoja voidaan siirtää
                  järjestelmiin, joita palokunta käyttää jäsenrekisterin
                  ylläpitoon ja viestintään.
                </p>
                <p class="font-weight-bold mb-1">REKISTERINPITÄJÄ</p>
                <p class="mb-0">Noormarkun VPK ry</p>
                <p class="mb-0">Palotie 4</p>
                <p class="mb-0">29600 Noormarkku</p>
                <p class="mb-2">
                  Sähköposti:
                  <a href="mailto:hallitus@noormarkunvpk.fi"
                    >hallitus@noormarkunvpk.fi</a
                  >
                </p>
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              x-large
              @click="submit"
              :loading="submitting"
              :disabled="!valid || !signatureUrl"
            >
              Lähetä lomake
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Success View -->
        <v-dialog v-model="successDialog" persistent max-width="500">
          <v-card>
            <v-card-title class="headline text-center">Kiitos!</v-card-title>
            <v-card-text class="text-center">
              <v-icon color="success" size="64" class="mb-4"
                >mdi-check-circle-outline</v-icon
              >
              <p>Lomake on lähetetty onnistuneesti.</p>
            </v-card-text>
            <v-card-actions class="justify-center pb-4">
              <v-btn color="primary" @click="finish"> Palaa etusivulle </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { firestore as db } from "@/plugins/firebase.app";
import api from "@/api";
import axios from "axios"; // Added axios
import dayjs from "dayjs";
import VueSignaturePad from "vue-signature-pad";
import ResponsiveToolbarTitle from "@/components/ResponsiveToolbarTitle.vue";

export default {
  name: "PublicMemberForm",
  components: {
    ResponsiveToolbarTitle,
    // Assuming VueSignaturePad is globally registered or I need to import it?
    // In ExpenseClaim it was used as <VueSignaturePad>.
    // If it fails, I might need to locally register it if not global.
    // Usually standard Vue plugins are global.
  },
  props: {
    // route param
  },
  data() {
    return {
      loading: true,
      submitting: false,
      error: null,
      docId: null,
      formData: null, // The firestore doc data
      menu: false,
      dobFormatted: "",
      form: {
        dob: null,
        address: "",
        phone: "",
        email: "",
        iban: "",
        allergies: "",
        drivingLicense: "",
        civilOccupation: "",
        professionalDriverQualification: false,
        guardianName: "",
        guardianPhone: "",
        guardianEmail: "",
        consentDataProcessing: false,
        consentImages: false,
        consentNamePublish: false,
        consentGuardian: false,
        qualifications: "",
        sizes: "",
        alarmGroups: "",
      },
      signatureUrl: null,
      valid: false,
      successDialog: false,

      requiredRules: [(v) => !!v || "Pakollinen kenttä"],
      emailRules: [
        (v) => !v || /.+@.+\..+/.test(v) || "Virheellinen sähköposti",
      ],
      ibanRules: [
        (v) => !!v || "IBAN vaaditaan",
        (v) =>
          /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(
            (v || "").replace(/\s/g, ""),
          ) || "Virheellinen IBAN",
      ],
    };
  },
  computed: {
    currentDate() {
      return dayjs().format("DD.MM.YYYY");
    },
    departmentsStr() {
      if (!this.formData) return "";
      return (this.formData.departments || []).join(", ");
    },
    isMinor() {
      if (!this.form.dob) return false;
      const birthDate = dayjs(this.form.dob);
      const age = dayjs().diff(birthDate, "year");
      return age < 18;
    },
    showIban() {
      if (!this.formData || !this.formData.departments) return false;
      const isHalytysosasto =
        this.formData.departments.includes("Hälytysosasto");
      return isHalytysosasto && !this.isMinor;
    },
  },
  async mounted() {
    const token = this.$route.params.token;
    if (!token) {
      this.error = "Virheellinen linkki";
      this.loading = false;
      return;
    }

    try {
      // Get by token using backend function (onRequest) which verifies JWT
      // Using standard HTTP request since it's an onRequest function now
      const isDev =
        process.env.NODE_ENV === "development" ||
        location.hostname === "localhost";
      const baseUrl = isDev
        ? "http://localhost:5001/appnoormarkunvpkfi/europe-west1"
        : "https://europe-west1-appnoormarkunvpkfi.cloudfunctions.net";

      const result = await axios.get(
        `${baseUrl}/getMemberFormInvitation?token=${token}`,
      );
      const data = result.data;

      // Check expiry - also done in backend but good to show UI error
      const expiresAt = data.expiresAt ? new Date(data.expiresAt) : null;
      if (expiresAt && dayjs().isAfter(dayjs(expiresAt))) {
        this.error = "Linkki on vanhentunut.";
        this.loading = false;
        return;
      }

      this.formData = data; // stores basic info like departments

      // Pre-fill email, phone if they were in the request? Admin created with Email.
      this.form.email = data.email || "";
    } catch (e) {
      console.error(e);
      // Axios error handling
      if (e.response && e.response.data && e.response.data.error) {
        this.error = e.response.data.error;
      } else {
        this.error = e.message || "Virhe ladattaessa lomaketta.";
      }
    } finally {
      this.loading = false;
    }

    // Watch dob for age check initially?
    this.$watch("form.dob", (newVal) => {
      this.dobFormatted = this.formatDate(this.form.dob);
      if (this.$refs.picker) this.$refs.picker.activePicker = "YEAR";
    });

    this.$watch("showIban", (val) => {
      if (!val) {
        this.form.iban = "";
      }
    });
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}.${month}.${year}`;
    },
    parseDate(date) {
      if (!date) return null;
      // Handle various separators
      const parts = date.split(/[.\-/]/);
      if (parts.length !== 3) return null;
      let [day, month, year] = parts;

      // Basic validation
      if (day.length === 1) day = "0" + day;
      if (month.length === 1) month = "0" + month;
      if (year.length === 2) year = "20" + year; // Assumption, or 19 depending on age

      return `${year}-${month}-${day}`;
    },
    updateDob() {
      if (this.dobFormatted) {
        const parsed = this.parseDate(this.dobFormatted);
        if (parsed) {
          this.form.dob = parsed;
        }
      }
      this.checkAge();
    },
    checkAge() {
      this.menu = false;
      // Validations update automatically due to computed isMinor used in rules
      this.$refs.form.validate();
    },
    clearSignaturePad() {
      this.$refs.signaturePad.clearSignature();
    },
    undoSignaturePad() {
      this.$refs.signaturePad.undoSignature();
    },
    saveSignaturePad() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      if (isEmpty) return;
      this.signatureUrl = data;
    },
    clearSignature() {
      this.signatureUrl = null;
    },
    async submit() {
      if (!this.$refs.form.validate() || !this.signatureUrl) {
        this.$toast.error(
          "Tarkista, että kaikki pakolliset kentät ja allekirjoitus on täytetty.",
        );
        return;
      }

      this.submitting = true;
      try {
        const token = this.$route.params.token;
        const isDev =
          process.env.NODE_ENV === "development" ||
          location.hostname === "localhost";
        const baseUrl = isDev
          ? "http://localhost:5001/appnoormarkunvpkfi/europe-west1"
          : "https://europe-west1-appnoormarkunvpkfi.cloudfunctions.net";

        // Call backend function (onRequest)
        await axios.post(`${baseUrl}/submitMemberForm`, {
          token: token,
          formData: this.form,
          signatureBase64: this.signatureUrl,
        });

        this.successDialog = true;
      } catch (e) {
        console.error(e);
        let msg = "Lähetys epäonnistui.";
        if (e.response && e.response.data && e.response.data.error) {
          msg += " " + e.response.data.error;
        } else {
          msg += " " + (e.message || "Tuntematon virhe");
        }
        this.$toast.error(msg);
      } finally {
        this.submitting = false;
      }
    },
    finish() {
      if (this.$pwa) {
        this.$router.push("/");
      } else {
        window.location.href = "https://noormarkunvpk.fi";
      }
    },
  },
};
</script>

<style scoped>
.word-break {
  word-break: break-word;
}
</style>
